import { Link, useLocation } from "react-router-dom";
import SeoHead from "../../components/Seo/SeoHead";
import { useAuth } from "../../auth/AuthContext";
import styles from "./NotFound.module.css";

const SITE_URL = "https://nexustech.tec.br";

/**
 * Sessão ativa é lida do `AuthContext`. Hoje o `<AuthProvider>` envolve o
 * `<Router>` inteiro em `App.js`, então a rota catch-all SEMPRE tem contexto —
 * mas `useAuth()` lança quando não tem, e uma página de erro que quebra ao
 * renderizar é o pior resultado possível. O try/catch degrada para "sem sessão"
 * (esconde o botão do painel) em vez de derrubar a tela.
 *
 * O `useContext` interno do `useAuth` já executou antes do throw, então a ordem
 * dos hooks continua estável entre renders.
 */
function useOptionalAuth() {
  try {
    return useAuth();
  } catch {
    return { isAuthenticated: false };
  }
}

/**
 * NotFound — rota catch-all (`path="*"`) do React Router.
 *
 * ⚠️ Numa SPA o servidor já respondeu `200` com o `index.html` antes de o React
 * montar (rewrite da Vercel). Esta página NÃO tenta forjar um `404` no cliente:
 * status HTTP real é configuração de hosting, não de componente. O que dá para
 * fazer aqui é `noindex,follow`, para o erro não entrar no índice de busca.
 */
export default function NotFound() {
  const { pathname } = useLocation();
  const { isAuthenticated } = useOptionalAuth();

  return (
    <>
      <SeoHead
        title="Página não encontrada | Nexus Chatbot"
        description="O link que você abriu não existe no Nexus Chatbot. Volte para a home ou vá direto para o painel."
        canonical={`${SITE_URL}${pathname}`}
        noindex
      />

      <section className={styles.wrap}>
        <div className={styles.card}>
          <p className={styles.badge}>Erro 404</p>

          <h1 className={styles.title}>
            Essa página a gente não conseguiu agendar
          </h1>

          <p className={styles.text}>
            O link que você abriu não existe por aqui. Mas o resto do Nexus
            continua no lugar.
          </p>

          <div className={styles.actions}>
            <Link to="/" className={styles.ctaPrimary}>
              Voltar para a home
            </Link>

            {isAuthenticated && (
              <Link to="/visao-geral" className={styles.ctaSecondary}>
                Ir para o painel
              </Link>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
